// Login.jsx
import React from "react";
import { Form, Input, Button, Spin, Card, message, Progress } from "antd";
import { connect } from "react-redux";

import API from "Util/Axios";
import { UserLogin } from "Redux/actions/User";
import { CDN } from "Constants";
import { getHttpError } from "Util/index.js";

import logo from "../assets/logo_remove_bg.png";

/* ==================================================================
   CONSTANTES
   ================================================================== */
const TOKEN_KEY = "token";
// T7/100A01: ya no se restringe el dominio; los clientes también tienen usuario.
// T7/100A310: la política de datos salió del login (2026-11-04); se publica en el sitio web.

/* ==================================================================
   HELPERS DE TOKEN
   ================================================================== */
const persistToken = (token) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* noop */
  }
};

const clearToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* noop */
  }
};

/* ==================================================================
   VALIDACIÓN DE DOMINIO
   ================================================================== */
// T7/100A01: retirado; bloqueaba a los clientes. El backend valida que el usuario exista y esté activo.
// const isAllowedEmail = (email) =>
//   typeof email === "string" &&
//   email.trim().toLowerCase().endsWith(ALLOWED_DOMAIN);

/* ==================================================================
   MEDIDOR DE FUERZA DE CONTRASEÑA
   ================================================================== */
const passwordStrength = (password) => {
  if (!password) return { label: "", color: "#d9d9d9", percent: 0 };

  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { label: "Débil",   color: "#ff4d4f", percent: 25  };
  if (score <= 2) return { label: "Media",   color: "#faad14", percent: 50  };
  if (score <= 3) return { label: "Alta",    color: "#52c41a", percent: 75  };
  return              { label: "Robusta", color: "#389e0d", percent: 100 };
};

/* ==================================================================
   ESTILOS
   ================================================================== */
const styles = {
  container: {
    width: "100vw",
    minHeight: "100vh",
    backgroundColor: "#D6DBDF",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
    boxSizing: "border-box",
  },
  card: { width: "100%", maxWidth: 400 },
  logo: { width: 200, margin: "0 auto 64px", display: "block" },
};

/* ==================================================================
   COMPONENTE
   ================================================================== */
class Login extends React.Component {
  state = {
    loading: false,
    passwordValue: "",
  };

  submittingRef = false;
  formRef = React.createRef();
  _mounted = false;

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  handleValuesChange = (changed) => {
    if (Object.prototype.hasOwnProperty.call(changed, "password")) {
      this.setState({ passwordValue: changed.password || "" });
    }
  };

  handleFinishFailed = ({ errorFields }) => {
    const first = errorFields?.[0];
    if (first?.errors?.length) {
      message.warning(first.errors[0]);
    }
  };

  handleFinish = async (values) => {
    if (this.submittingRef) return;

    // 1) Sanitizamos el email directamente en el objeto original
    const email = String(values.email ?? "").trim().toLowerCase();
    
    // T7/100A01: retirado el bloqueo por dominio (los clientes tienen usuario).
    // if (!isAllowedEmail(email)) {
    //   message.error(`Solo se permiten correos con dominio ${ALLOWED_DOMAIN}`);
    //   return;
    // }

    // 2) Mantenemos el objeto idéntico al original (con remember, etc)
    values.email = email;

    // 3) Bloqueamos el submit y activamos el loading de inmediato
    this.submittingRef = true;
    this.setState({ loading: true });

    try {
      // 4) Enviamos `values` tal cual lo hacía tu código original
      const response = await API.post("login", values);
      const token = response?.data;

      if (!token || typeof token !== "string") {
        throw new Error("Respuesta inválida del servidor");
      }

      persistToken(token);
      this.props.UserLogin(true, token);
      
    } catch (error) {
      clearToken();
      this.props.UserLogin(false, "");

      this.formRef.current?.setFieldsValue({ password: "" });
      if (this._mounted) this.setState({ passwordValue: "" });

      let errMsg = "No se pudo iniciar sesión. Verifique sus credenciales o contacte a soporte.";
      try {
        const apiMsg = getHttpError(error);
        if (apiMsg && typeof apiMsg === "string") errMsg = apiMsg;
      } catch {
        /* conservar fallback */
      }
      
      // Si el servidor responde 404, avisamos claramente
      if (error?.response?.status === 404) {
        errMsg = "Error 404: El servidor no encuentra la ruta de login (API).";
      }

      message.error(errMsg);
    } finally {
      // 5) Siempre liberamos el loading al finalizar (éxito o error)
      this.submittingRef = false;
      if (this._mounted) this.setState({ loading: false });
    }
  };

  render() {
    const { loading, passwordValue } = this.state;
    const strength = passwordStrength(passwordValue);

    return (
      <div style={styles.container}>
        <Card style={styles.card}>
          <img
            src={logo}
            alt="Logo de la empresa"
            style={styles.logo}
          />

          {/* MEJORA: Se eliminó delay={200} para que el Spin aparezca inmediatamente */}
          <Spin spinning={loading}>
            <Form
              ref={this.formRef}
              name="basic"
              layout="vertical"
              initialValues={{
                remember: true,
                email: "",
                clave: "",
              }}
              onFinish={this.handleFinish}
              onFinishFailed={this.handleFinishFailed}
              onValuesChange={this.handleValuesChange}
              autoComplete="on"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                // T7/100A01: original "E-mail corporativo"; ahora entran también clientes.
                label="E-mail"
                normalize={(v) => (typeof v === "string" ? v.trim() : v)}
                rules={[
                  { required: true, message: "Por favor ingrese su E-mail" },
                  { type: "email", message: "Formato de E-mail inválido" },
                  { max: 254, message: "E-mail demasiado largo" },
                  // T7/100A01: retirado el validador de dominio @jagsas.com.
                  // {
                  //   validator: (_, value) => {
                  //     if (!value) return Promise.resolve();
                  //     if (isAllowedEmail(value)) return Promise.resolve();
                  //     return Promise.reject(
                  //       new Error(`Debe usar un correo corporativo (${ALLOWED_DOMAIN})`)
                  //     );
                  //   },
                  // },
                ]}
              >
                <Input
                  type="email"
                  placeholder="usuario@empresa.com" // T7/100A01: original `usuario${ALLOWED_DOMAIN}`
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={254}
                  disabled={loading}
                  autoFocus
                  aria-label="E-mail"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Contraseña"
                rules={[
                  { required: true, message: "Por favor ingrese su contraseña!" },
                  { max: 128, message: "Contraseña demasiado larga" },
                ]}
                extra={
                  passwordValue ? (
                    <div style={{ marginTop: 4 }}>
                      <Progress
                        percent={strength.percent}
                        strokeColor={strength.color}
                        showInfo={false}
                        size="small"
                      />
                      <span
                        style={{
                          color: strength.color,
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        Seguridad: {strength.label}
                      </span>
                    </div>
                  ) : null
                }
              >
                <Input.Password
                  placeholder="Contraseña"
                  autoComplete="current-password"
                  maxLength={128}
                  disabled={loading}
                  aria-label="Contraseña"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                {/* MEJORA: Se agregó disabled={loading} para bloquear clics adicionales */}
                <Button
                  type="primary"
                  block
                  htmlType="submit"
                  loading={loading}
                  disabled={loading}
                >
                  Acceder
                </Button>
              </Form.Item>
            </Form>
          </Spin>
        </Card>
      </div>
    );
  }
}

const mapToAction = (dispatch) => ({
  UserLogin: (authed, token) => {
    dispatch(UserLogin(authed, token));
  },
});

export default connect(null, mapToAction)(Login);